import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const AIRCRAFT_AVAILABILITY_NEAR_MINUTES = 30;

function formatUtcWithMicrosecondOffset(epochMilliseconds, direction) {
    if (direction < 0) {
        return `${dayjs.utc(epochMilliseconds - 1).format("YYYY-MM-DDTHH:mm:ss.SSS")}999Z`;
    }
    return `${dayjs.utc(epochMilliseconds).format("YYYY-MM-DDTHH:mm:ss.SSS")}001Z`;
}

export function normalizeAircraftRegistration(value) {
    return String(value || "").trim().toUpperCase();
}

export function normalizeAircraftRegistrations(registrations = []) {
    return [...new Set(registrations.map(normalizeAircraftRegistration).filter(Boolean))].sort();
}

export function getAircraftAvailabilityWindows(windowsByRegistration = {}, aircraftRegistration) {
    const normalizedRegistration = normalizeAircraftRegistration(aircraftRegistration);
    if (!normalizedRegistration || !windowsByRegistration) {
        return [];
    }
    const windows = windowsByRegistration instanceof Map
        ? windowsByRegistration.get(normalizedRegistration)
        : windowsByRegistration[normalizedRegistration] || windowsByRegistration[aircraftRegistration];
    return Array.isArray(windows) ? windows : [];
}

export function aircraftAvailabilityIntervalsOverlap(flightStart, flightEnd, windowStart, windowEnd) {
    return flightEnd.isAfter(windowStart) && (!windowEnd || flightStart.isBefore(windowEnd));
}

export function getAircraftAvailabilityGap(flightStart, flightEnd, windowStart, windowEnd) {
    if (!flightStart.isValid() || !flightEnd.isValid() || !windowStart.isValid()) {
        return null;
    }
    if (!flightEnd.isAfter(windowStart)) {
        return {
            gapMinutes: Math.max(0, Math.ceil(windowStart.diff(flightEnd, "second") / 60)),
            nearSide: "before_start",
        };
    }
    if (windowEnd?.isValid?.() && !flightStart.isBefore(windowEnd)) {
        return {
            gapMinutes: Math.max(0, Math.ceil(flightStart.diff(windowEnd, "second") / 60)),
            nearSide: "after_end",
        };
    }
    return null;
}

export function evaluateAircraftAvailabilityWindow({
    flightStart,
    flightEnd,
    window,
    nearMinutes = AIRCRAFT_AVAILABILITY_NEAR_MINUTES,
} = {}) {
    if (String(window?.status || "ACTIVE").trim().toUpperCase() !== "ACTIVE" || !window?.start_time) {
        return { relation: "none", window };
    }

    const normalizedFlightStart = dayjs.utc(flightStart);
    const normalizedFlightEnd = dayjs.utc(flightEnd);
    const windowStart = dayjs.utc(window.start_time);
    const windowEnd = window?.end_time ? dayjs.utc(window.end_time) : null;
    if (
        !normalizedFlightStart.isValid()
        || !normalizedFlightEnd.isValid()
        || !normalizedFlightStart.isBefore(normalizedFlightEnd)
        || !windowStart.isValid()
        || (windowEnd && !windowEnd.isValid())
    ) {
        return { relation: "none", window };
    }

    if (aircraftAvailabilityIntervalsOverlap(normalizedFlightStart, normalizedFlightEnd, windowStart, windowEnd)) {
        return {
            relation: "overlap",
            window,
            flightStart: normalizedFlightStart,
            flightEnd: normalizedFlightEnd,
            windowStart,
            windowEnd,
            overlapStart: normalizedFlightStart.isAfter(windowStart) ? normalizedFlightStart : windowStart,
            overlapEnd: windowEnd && windowEnd.isBefore(normalizedFlightEnd) ? windowEnd : normalizedFlightEnd,
        };
    }

    const gap = getAircraftAvailabilityGap(normalizedFlightStart, normalizedFlightEnd, windowStart, windowEnd);
    if (!gap || gap.gapMinutes > nearMinutes) {
        return { relation: "none", window };
    }
    return {
        relation: "near",
        window,
        flightStart: normalizedFlightStart,
        flightEnd: normalizedFlightEnd,
        windowStart,
        windowEnd,
        ...gap,
    };
}

export function evaluateAircraftAvailability({
    flightStart,
    flightEnd,
    windows = [],
    nearMinutes = AIRCRAFT_AVAILABILITY_NEAR_MINUTES,
} = {}) {
    return windows
        .map((window) => evaluateAircraftAvailabilityWindow({
            flightStart,
            flightEnd,
            window,
            nearMinutes,
        }))
        .filter((result) => result.relation !== "none");
}

export function buildAircraftAvailabilityQuery({
    flights = [],
    nearMinutes = AIRCRAFT_AVAILABILITY_NEAR_MINUTES,
} = {}) {
    const validFlights = flights.filter((flight) => {
        const aircraftRegistration = normalizeAircraftRegistration(flight?.aircraft_reg);
        const start = dayjs.utc(flight?.std);
        const end = dayjs.utc(flight?.sta);
        return aircraftRegistration && start.isValid() && end.isValid() && start.isBefore(end);
    });
    const registrations = normalizeAircraftRegistrations(
        validFlights.map((flight) => flight?.aircraft_reg),
    );
    if (!validFlights.length || !registrations.length) {
        return { registrations, start: null, end: null };
    }

    const starts = validFlights.map((flight) => dayjs.utc(flight.std).valueOf());
    const ends = validFlights.map((flight) => dayjs.utc(flight.sta).valueOf());
    const marginMs = nearMinutes * 60 * 1000;
    return {
        registrations,
        start: formatUtcWithMicrosecondOffset(Math.min(...starts) - marginMs, -1),
        end: formatUtcWithMicrosecondOffset(Math.max(...ends) + marginMs, 1),
    };
}
